import { Avatar, Box, Flex, Text } from "@chakra-ui/react";
import { FeedCardHalo } from "./FeedCardHalo";
import { FeedCreatorEntry, FeedCreatorHighlightItem } from "./types";

interface FeedCreatorHighlightClusterProps {
  item: FeedCreatorHighlightItem;
  isActive: boolean;
}

/**
 * "Creator spotlight" cluster — one fullscreen slot composed of N creator
 * rows. Each row is a tap target to that creator's page.
 */
export function FeedCreatorHighlightCluster({
  item,
  isActive,
}: FeedCreatorHighlightClusterProps) {
  const heroSrc = item.creators[0]?.avatar_url ?? null;

  return (
    <Box position="relative" w="100%" h="100%">
      <FeedCardHalo src={heroSrc} />
      <Box
        position="relative"
        zIndex={1}
        w="100%"
        h="100%"
        bgGradient="linear(135deg, rgba(40, 24, 60, 0.95) 0%, rgba(16, 14, 24, 0.95) 100%)"
        borderRadius="2xl"
        border="1px solid rgba(255,255,255,0.06)"
        color="white"
        overflow="hidden"
        p={3}
      >
        <Flex direction="column" gap={3} h="100%">
          {item.creators.map((c) => (
            <CreatorRow
              key={c.artist_id}
              entry={c}
              isActive={isActive}
            />
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

function CreatorRow({
  entry,
  isActive,
}: {
  entry: FeedCreatorEntry;
  isActive: boolean;
}) {
  const showStats = entry.follower_count > 0 || entry.work_count > 0;
  return (
    <Flex
      as="a"
      href={entry.artist_url}
      tabIndex={isActive ? 0 : -1}
      direction="row"
      align="center"
      gap={4}
      flex="1"
      minH="0"
      px={4}
      py={3}
      bg="rgba(28, 18, 42, 0.6)"
      borderRadius="xl"
      cursor="pointer"
      _hover={{ bg: "rgba(40, 26, 58, 0.7)" }}
      transition="background 120ms ease"
    >
      <Avatar
        size="lg"
        src={entry.avatar_url ?? undefined}
        name={entry.artist_name}
        bg="purple.500"
        flexShrink={0}
        boxShadow="0 8px 20px rgba(0,0,0,0.4)"
      />
      <Flex direction="column" gap={0.5} flex="1" minW="0">
        <Text fontSize="lg" fontWeight="bold" lineHeight="1.2" noOfLines={1}>
          {entry.artist_name}
        </Text>
        {entry.description ? (
          <Text fontSize="xs" opacity={0.7} noOfLines={1}>
            {entry.description}
          </Text>
        ) : null}
        {showStats ? (
          <Flex align="baseline" gap={3} fontSize="xs" opacity={0.85} mt={1}>
            {entry.follower_count > 0 ? (
              <StatPair
                label={
                  entry.follower_count === 1 ? "follower" : "followers"
                }
                value={entry.follower_count}
              />
            ) : null}
            {entry.work_count > 0 ? (
              <StatPair
                label={entry.work_count === 1 ? "work" : "works"}
                value={entry.work_count}
              />
            ) : null}
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
}

function StatPair({ value, label }: { value: number; label: string }) {
  return (
    <Flex align="baseline" gap={1}>
      <Text fontWeight="bold" color="white">
        {formatCount(value)}
      </Text>
      <Text>{label}</Text>
    </Flex>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000)
    return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "K";
  return String(n);
}
